// src/utils/jobSearch.js
// calpol-9c4e7b23 — ONE definition of "does this job match what was typed".
//
// WHY THIS FILE EXISTS. The predicate lived as a local const inside
// JobManagementView (`matchesJobSearch`, previously :655-660) and seam 4 adds a
// second search box on the calendar. A hand-written second copy is how the two
// drift — the Jobs list and the Calendar would start disagreeing about whether
// "#1860" finds job 1860. This mirrors the precedent already set by
// customerDisplay.js:47 (`customerMatchesQuery`), which CustomerManagementView
// and CustomerCombobox both import rather than each carrying their own.
//
// CONTRACT: the caller pre-normalises the term (trim + toLowerCase); this does
// NOT lowercase it for you. That is the shape JobManagementView already called
// with at :681 and :816, and keeping it means the term is lowercased once per
// memo rather than once per job.
//
// FIELD NAMES, verified from disk rather than assumed:
//   • job number  -> `jobNumber` (a number, hence String()). There is no
//     `jobNum` / `displayNumber` anywhere in the repo. The leading '#' is a
//     DISPLAY convention only (formatters.js:68-71, ExpensesView.js:106) and is
//     not part of the stored value.
//   • customer    -> `clientName` on a JOB. (`customerName` is the invoice /
//     quote / recurring-schedule field — see CrmApp.js:1181, which translates
//     quote.customerName into job.clientName at conversion.)

// Two deliberate widenings over the original JobManagementView predicate. Both
// only ever make MORE jobs findable, so neither can hide a row that is findable
// today — which is why they are safe to apply to the Jobs list at the same time.
//
//   1. A leading '#' is stripped from the term. Every surface in the app PRINTS
//      the number as "#1860", so typing it back is the obvious thing to do, and
//      it used to match nothing at all.
//   2. `customerDetails?.name` is searched as well as `clientName`. The Jobs
//      list DISPLAYS `clientName || customerDetails?.name`
//      (JobManagementView.js:282) but only ever SEARCHED `clientName`, so a job
//      carrying just the nested name was visible on screen and unfindable by it.
export const matchesJobSearch = (job, term) => {
    if (!job || !term) return false;
    // Only a LEADING '#', so a term that legitimately contains one mid-string
    // still matches a title character-for-character.
    const bare = term.startsWith('#') ? term.slice(1) : term;
    if (!bare) return false;
    return (
        job.title?.toLowerCase().includes(bare)
        || job.clientName?.toLowerCase().includes(bare)
        || job.customerDetails?.name?.toLowerCase().includes(bare)
        || String(job.jobNumber ?? '').toLowerCase().includes(bare)
    );
};

// qfupcal-6e3b9d47 — the same question, asked of a QUOTE.
//
// WHY NOT REUSE matchesJobSearch: the field names genuinely differ, and none of
// them overlap. A quote has `quoteNumber` (a STRING, already carrying its 'E-'
// or 'Q-' prefix from the server) where a job has a numeric `jobNumber`; and it
// has `customerName` where a job has `clientName` — that rename is deliberate
// and happens at conversion (CrmApp.js). Passing a quote to matchesJobSearch
// would silently match on nothing but `title`.
//
// Same CONTRACT as above: the caller pre-normalises the term (trim +
// toLowerCase), and a leading '#' is stripped for the same reason.
//
// THE '#' AND 'E-' CASE: quote numbers are printed as "E-0007", so the natural
// thing to type is "e-0007" or just "0007" — both work, because this is a plain
// substring test against the stored string. Typing "7" also matches, which is
// the same looseness the job search already has.
export const matchesQuoteSearch = (quote, term) => {
    if (!quote || !term) return false;
    const bare = term.startsWith('#') ? term.slice(1) : term;
    if (!bare) return false;
    return (
        quote.title?.toLowerCase().includes(bare)
        || quote.customerName?.toLowerCase().includes(bare)
        || quote.customerDetails?.name?.toLowerCase().includes(bare)
        || String(quote.quoteNumber ?? '').toLowerCase().includes(bare)
    );
};
